import type {
  PathOutcome,
  Projection,
  ProjectionPath,
  ProjectionUnavailableReason,
} from '~/calculations/projection'
import { InfoButton } from '~/components/primitives/InfoButton'
import type { WeightDisplayUnit } from '~/models/settings'
import type { Percent } from '~/types/units'
import {
  formatApproximateMonth,
  formatPercentChange,
  formatWeekCount,
  formatWeekSpan,
  formatWeight,
} from '~/utils/format'
import styles from './ProjectionSummary.module.css'

/**
 * Goal projection (D-068).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS CONTEXT, AND THE COPY HAS TO KEEP SAYING SO.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Three rules govern every string in this file:
 *
 *   - **no date.** A span of weeks and an approximate month. The value carries
 *     no day, so there is none to render;
 *   - **no appearance outcome.** Weight terms only. Nothing here may suggest
 *     visible abs, a look, or a guaranteed result — the constraint L-001 carried
 *     from the start;
 *   - **no negative or unbounded duration.** A path that is not moving toward
 *     the goal says exactly that.
 *
 * The observed line is deliberately separated from the three plan lines. It is
 * the only one reporting reality, and running them together would let a plan
 * read as an achievement.
 */

interface ProjectionSummaryProps {
  projection: Projection
  unit: WeightDisplayUnit
}

const PATH_LABELS: Readonly<Record<ProjectionPath['key'], string>> = {
  conservative: 'Conservative',
  recommended: 'Recommended',
  faster: 'Faster',
  observed: 'Your pace so far',
}

const UNAVAILABLE_COPY: Readonly<Record<ProjectionUnavailableReason, string>> = {
  notEnoughWeighIns:
    'A projection needs a seven-day average to work from. Weigh in on a few more days and it will appear here.',
  noGoalTargetWeight:
    'Your goal has no target weight, so there is nothing to project towards. Add one in Settings if you want an estimate.',
  noActivePhase:
    'A projection reads the weekly target range from your active phase. Set a phase up and it will appear here.',
}

export function ProjectionSummary({ projection, unit }: ProjectionSummaryProps) {
  if (projection.kind === 'unavailable') {
    return (
      <Card>
        <p class={styles.empty}>{UNAVAILABLE_COPY[projection.reason]}</p>
      </Card>
    )
  }

  if (projection.kind === 'alreadyWithinTarget') {
    return (
      <Card>
        <p class={styles.headline}>You are inside your target weight range.</p>
        <p class={styles.meta}>
          Your seven-day average is {formatWeight(projection.anchorKg, unit)}. There is nothing left
          to project — what happens next is a phase decision, not a distance.
        </p>
      </Card>
    )
  }

  const recommended = projection.plan.find((path) => path.key === 'recommended')
  const direction = projection.target.direction === 'loss' ? 'down to' : 'up to'

  return (
    <Card>
      {projection.span === undefined ? (
        <p class={styles.headline}>
          None of your phase's target range moves you towards{' '}
          {formatWeight(projection.target.weightKg, unit)}.
        </p>
      ) : (
        <>
          <p class={styles.headline}>
            {formatWeekSpan(projection.span)}
            {recommended?.outcome.kind === 'reaches'
              ? ` — around ${formatApproximateMonth(recommended.outcome.approximateMonth)}`
              : ''}
          </p>
          <p class={styles.meta}>
            From {formatWeight(projection.anchorKg, unit)} {direction}{' '}
            {formatWeight(projection.target.weightKg, unit)}, at your phase's own weekly target
            range.
          </p>
        </>
      )}

      <dl class={styles.paths}>
        {projection.plan.map((path) => (
          <PathRow key={path.key} path={path} />
        ))}
      </dl>

      <div class={styles.observed}>
        {projection.observed === undefined ? (
          <p class={styles.meta}>
            Your actual pace needs two full weeks of weigh-ins before it can be compared. Until
            then, only the plan is shown.
          </p>
        ) : (
          <dl class={styles.paths}>
            <PathRow path={projection.observed} />
          </dl>
        )}
      </div>

      {/* Never a promise, and never about appearance. The phrasing states the
          scope positively — "weight only" — rather than naming the outcome it
          is refusing to predict, which would put that outcome in the user's
          head anyway. */}
      {/* The claim stays visible and the method is one tap away (D-104 §5). A
          projection is the one figure on this screen a user could mistake for a
          plan, so what it is not has to be readable without opening anything. */}
      <div class={styles.caveatRow}>
        <p class={styles.caveat}>An estimate, not a schedule. It covers weight only.</p>
        <InfoButton topic="this estimate" title="How this estimate works">
          <p>
            It reads your seven-day average and your phase's own weekly target range, and reports
            the arithmetic between them. It moves as your weight does.
          </p>
          <p>
            It is never a recommendation and nothing acts on it — the rules engine does not read it,
            and reaching a projected month changes nothing on its own. Dates are given to the month
            because a projection cannot be accurate to a day.
          </p>
        </InfoButton>
      </div>
    </Card>
  )
}

function PathRow({ path }: { path: ProjectionPath }) {
  return (
    <div class={styles.pathRow}>
      <dt class={styles.pathLabel}>
        {PATH_LABELS[path.key]}
        {/* A rate, already finite by construction — the brand is carried across
            rather than reconstructed, exactly as the calculations do. */}
        <span class={styles.pathRate}>
          {' '}
          · {formatPercentChange(path.weeklyRatePercent as Percent)} a week
        </span>
      </dt>
      <dd class={styles.pathValue}>{outcomeText(path.outcome)}</dd>
    </div>
  )
}

/**
 * The four outcomes in words.
 *
 * The three non-arriving states are distinct sentences rather than one shrug,
 * because they mean different things and lead to different decisions.
 */
function outcomeText(outcome: PathOutcome): string {
  switch (outcome.kind) {
    case 'reaches':
      return `${formatWeekCount(outcome.weeks)} · around ${formatApproximateMonth(outcome.approximateMonth)}`
    case 'movingAway':
      return 'Moves away from your target'
    case 'notMoving':
      return 'Holds your weight where it is'
    case 'beyondHorizon':
      return 'Too slow to estimate'
  }
}

function Card({ children }: { children: preact.ComponentChildren }) {
  return (
    <section class={styles.card} aria-labelledby="projection-heading">
      <h2 id="projection-heading" class={styles.heading}>
        Goal projection
      </h2>
      {children}
    </section>
  )
}
